(function () {
  'use strict';

  d3.chart('ChartBase').extend('Plot', {
    initialize : function (options) {
      var plot = this;
      options = options || {};

      _.defaults(options, {
        xAxis : true,
        yAxis : true,
        xFormat : null,
        yFormat : null,
        showGrid : true
      });

      if (options.contextMode) {
        options.yAxis = false;
        options.showGrid = false;
      }

      // set default padding, but don't override the ones set by options
      plot.padding({
        left : 50,
        bottom : 30
      });
      plot.padding(options.padding);

      /*
       * Lowest-level accessors that get values from a datum.
       * You can override these with an options.fn object.
       *
       * Each series can also override these functions to
       * access different fields of the same data array.
       *
       * When drawing a series, use plot.withSeries so the
       * correct accessors are switched out.
       *
       * GOTCHA: Plots assume each series accesses `x` in the same way,
       * so if you override the `x` accessor, do it at the top level
       * rather than on a series.
       */
      plot.fn = plot.defaultFns = _.defaults({}, options.fn, {
        x : function (d) { return d.x; },
        y : function (d) { return d.y; }
      });

      plot.x = d3.time.scale();
      plot.y = d3.scale.linear();

      // allows for having a fixed y-range (e.g. always starting at 0).
      // GOTCHA: Requires each sub-class of Plot to implement this
      plot.yDomain = options.yDomain || {};

      plot.color = d3.scale.category10();

      plot.clipperId = '';

      // visualization group containing the graph series lines
      plot.viz = plot.wrap.append('g').classed('vis', true);

      // dark horizontal line at y = 0
      plot.centerAxis = plot.wrap.append('g').classed('centerAxis', !options.contextMode);
      plot.layer('centerAxis', plot.centerAxis, {
        dataBind : function (__) {
          var data = [0].filter(function (d) {
            var domain = plot.y.domain();
            return domain[0] < d && d < domain[1];
          });
          return this.selectAll('line')
              .data(data);
        },
        insert : function () {
          return this.insert('line');
        },
        events : {
          merge : function () {
            var domain = plot.x.domain();
            this.attr('x1', plot.x(domain[0]))
                .attr('x2', plot.x(domain[1]))
                .attr('y1', plot.y)
                .attr('y2', plot.y);
          }
        }
      });

      plot.axisSvgs = {
        x : plot.wrap.append('g').classed('axis x', true),
        y : plot.wrap.append('g').classed('axis y', true)
      };

      plot.axes = {
        x : d3.svg.axis().scale(plot.x).orient('bottom'),
        y : d3.svg.axis().scale(plot.y).orient('left')
      };

      if (!options.xAxis) {
        plot.axisSvgs.x.style('display', 'none');
      }

      if (!options.yAxis) {
        plot.axisSvgs.y.style('display', 'none');
      }

      if (options.xFormat) {
        plot.axes.x.tickFormat(options.xFormat);
      }

      if (options.yFormat) {
        plot.axes.y.tickFormat(options.yFormat);
      }

      // The function that defines what text to display on
      // the inspector tip as the first row (for the 'x' value).
      // Should return a pair of strings: [label, value].
      // `this` will be a reference to the `plot`
      plot.domainTipText = options.domainTipText ||
          function (d) {
            return ['Date', new Date(plot.fn.x(d)).toLocaleString()];
          };

      function calcXRange() {
        plot.x.range([0, plot.w]);

        if (options.showGrid) {
          plot.axes.y
              .tickPadding(10)
              .innerTickSize(-plot.w);
        }

        plot.axisSvgs.x
            .call(plot.axes.x);
      }

      function calcYRange() {
        plot.y.range([plot.h, 0]);

        if (options.showGrid) {
          plot.axes.x
              .tickPadding(10)
              .innerTickSize(-plot.h);
        }

        plot.axisSvgs.x
            .translate(0, plot.y.range()[0])
            .call(plot.axes.x);

        plot.axisSvgs.y
            .call(plot.axes.y);
      }

      plot
          .on('change:width', function () {
            calcXRange();
            plot.cullData();
          })
          .on('change:height', calcYRange)
          .on('change:data', function () {
            calcXRange();
            calcYRange();
          });
    },
    cullData : function () {
      var plot = this;

      var data = plot.data,
          allData = data.allData,
      // The domain of the full data set
          allDomain = data.allDomain,
      // The domain of the visible portion
          curDomain = plot.x.domain();

      // Compare scalar width of the visible data vs the full data set
      var allSize = allDomain[1] - allDomain[0],
          curSize = curDomain[1] - curDomain[0],
          visiblePercent = curSize / allSize;

      // Calculate the approximate number of data per pixel.
      // Anything more than 1 means we're drawing more data than we need to.
      var approxNewLength = allData.length * visiblePercent;
      var dataPerPixel = Math.max(1, Math.round(approxNewLength / plot.w));

      // Avoid needlessly culling data if the density hasn't changed
      if (plot.cullingFactor !== dataPerPixel) {
        plot.cullingFactor = dataPerPixel;

        plot.data.data = allData.filter(function (__, i) {
          // Cull the data to one datum per pixel
          return i % dataPerPixel === 0;
        });
      }
    },
    setXDomain : function (extent) {
      this.x.domain(extent);
      this.cullData();
    },
    setYDomain : function (extent) {
      var plot = this,
          maxAbs;
      if (plot.yDomain === 'centered') {
        // vertically center 0
        maxAbs = d3.max(extent.map(Math.abs));
        extent = [-maxAbs, maxAbs];
      }

      if (extent[0] === extent[1]) {
        // Avoid the very unlikely case where all y-values are the same.
        // When that happens, the y-axis becomes invisible.
        // This will (almost certainly) only ever happen when there's
        // a single data point or with fake data.
        extent[1] += 1;
      }

      this.y.domain(extent);
    },
    brushed : function (extent, clipperId) {
      var plot = this;

      plot.setXDomain(extent);

      plot.axisSvgs.x
          .call(plot.axes.x);
      plot.redraw();

      plot.clipperId = clipperId;
      plot.trigger('change:clip');

      return plot;
    },
    /**
     * Set the current set of datum accessors (`plot.fn`) to the
     * given series' functions. `plot.fn` will be reverted to `defaultFns`
     * after the series has used the data.
     *
     * If called with a single function, this returns a function
     * that expects a series as its first argument and will call `withSeries`
     * with the given function and the passed series. This is useful
     * when doing e.g. `data.series.each(plot.withSeries(function() {...})`
     *
     * In both cases, the current series will be passed to the callback.
     */
    withSeries : function (series, callback) {
      var plot = this, ret;
      if (typeof series === 'function') {
        // allows for mapping over series and doing the same thing with each one
        return function (s) {
          return plot.withSeries(s, series);
        };
      }
      plot.fn = series.fn;
      ret = callback(series);
      plot.fn = plot.defaultFns;
      return ret;
    },
    describeDatumUnder : function (coords) {
      var plot = this;
      var scaleX = plot.x.invert(coords[0]);
      var extent = plot.x.domain();

      if (!plot.data) {
        // plot.data is the only thing this class doesn't set.
        throw new Error('No plot.data -- did you forget to set it in the transform?');
      }

      var dx = plot.fn.x;
      // find the datum that's closest to the coords
      var datum = _(plot.data.data).filter(function (d) {
        // don't inspect anything outside of the current context
        var xVal = dx(d);
        return extent[0] <= xVal && xVal <= extent[1];
      }).min(function (d) {
        return Math.abs(dx(d) - scaleX);
      });

      // min() returns Infinity if the array was empty.
      // this can happen if there are no data within
      // the brush's extent
      if (datum === Infinity) {
        datum = null;
        return null;
      } else {
        var yVals = [];
        var tipTextRows = _.filter(plot.data.series, 'tipText').map(plot.withSeries(function (series) {

          yVals.push(plot.y(plot.fn.y(datum)));

          return [series.name, series.tipText.call(plot, datum)];
        }));

        return {
          x : plot.x(dx(datum)),
          y : yVals,
          text : [
              plot.domainTipText(datum)
          ].concat(tipTextRows)
        };
      }
    },
    // called when the plot receives a new set of data
    setData : function (data) {
      var plot = this;

      plot.data = data;

      // The ContextChart creates 2 instances of the same Plot.
      // The main plot, called the focus, will set `data.allData`.
      // The context plot also calls `setData`, but at that
      // point, `data.data` is the already culled data, so we
      // don't want to overwrite `allData`.
      if (!data.allData) {
        data.allData = data.data.slice();
        // cache the full domain because it won't change.
        data.allDomain = d3.extent(data.allData.map(plot.fn.x));
        // clear the previous cullingFactor so we always cull a new set of data.
        plot.cullingFactor = null;
      }

      _.each(data.series, function (series) {
        // allows a series to override the default data accessor functions
        series.fn = _.defaults(series.fn || {}, plot.defaultFns);
      });

      // resets the domain to the full data set
      plot.setXDomain(data.allDomain);

      plot.color.domain(data.series.map(_.property('name')));
    }
  });

})();
